import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import Lottie from 'react-lottie';
import styled from 'styled-components';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import animationData from '../../../public/animations/use-cases.json';
import smoothscroll from 'smoothscroll-polyfill';
import { isMobileOnly } from 'react-device-detect';

gsap.registerPlugin(ScrollTrigger);

export const UseCasesAnimation: FunctionComponent = () => {
  const animation = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [positionY, setPositionY] = useState(0);

  function setupClickListeners() {
    const ids = ['Connect', 'Discover', 'Build', 'Deploy'];
    const element = animation.current.el;

    ids.map((id, idx) => {
      const heading = element.querySelector(`g[aria-label="${id}"]`);

      heading.setAttribute('class', 'clickable');
      heading.addEventListener('click', () => {
        setSelectedIndex(idx);
      });
    });
  }

  function setSelectedIndex(index: number) {
    const scroll = (top: number) =>
      window?.document?.documentElement?.scrollTo({
        top: positionY + top,
        behavior: 'smooth',
      });

    switch (index) {
      case 0:
        return scroll(0);

      case 1:
        return scroll(300);

      case 2:
        return scroll(650);

      case 3:
        return scroll(1000);
    }
  }

  function setAnimationProgress(progress: number) {
    const percentage = parseInt(
      (animation.current?.anim?.totalFrames * progress).toFixed()
    );

    animation.current?.anim.goToAndStop(percentage, true);
  }

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const trigger = ScrollTrigger.create({
      trigger: containerRef.current,
      start: 'top top',
      end: '+=1000',
      pin: true,
      scrub: true,
      onUpdate: (self) => setAnimationProgress(self.progress),
    });

    return () => trigger.kill();
  }, []);

  if (isMobileOnly) {
    return null;
  }

  return (
    <Container
      id="animation"
      ref={(ref) => {
        (containerRef as any).current = ref;
        const { top } = ref?.getBoundingClientRect() ?? {};
        if (top && !positionY) {
          setPositionY(top);
        }
      }}
    >
      <Lottie
        ref={animation}
        isClickToPauseDisabled={true}
        isPaused={true}
        options={{
          autoplay: false,
          loop: false,
          animationData,
          rendererSettings: {
            preserveAspectRatio: 'xMinYMin slice',
            filterSize: {
              width: '200%',
              height: '200%',
              x: '-50%',
              y: '-50%',
            },
          } as any,
        }}
        eventListeners={[
          {
            eventName: 'DOMLoaded',
            callback: () => setupClickListeners(),
          },
        ]}
      />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  inset: auto !important;
  top: 0% !important;

  .clickable {
    cursor: pointer;

    &:hover {
      opacity: 0.5;
    }
  }
`;
